import React, { useState, useEffect } from "react";
import { noop } from "utils/General";
import { Editor } from "@tinymce/tinymce-react";
import { openFilePicker } from "utils/General/sagas";
import { prop, equals } from "ramda";
import { captureException } from "utils/errors";
import { TINY_MCE_EXTERNAL_PLUGINS } from "ui-kit/Form/constants";
import TextAreaEditor from "components/Global/StandAloneEditors/TextArea";

const handleImageUpload = async (insertUploadedImage, notifyError) => {
  const showError = (message = "Error uploading image") =>
    notifyError({ message });
  const uploadedImages = await openFilePicker({
    accept: ["image/*"],
    onFileUploadFailed: () => showError()
  });
  if (!uploadedImages.length) {
    showError();
    return;
  }
  try {
    insertUploadedImage(prop("url", uploadedImages[0]), {
      title: prop("filename", uploadedImages[0])
    });
  } catch {
    showError("Error inserting image");
  }
};

export const maxLengthPlugin = (editor, maxLength) => {
  const countCharacters = () => {
    const count =
      editor.plugins.wordcount && editor.plugins.wordcount.body
        ? editor.plugins.wordcount.body.getCharacterCountWithoutSpaces()
        : editor.getContent({ format: "text" }).length;
    return count;
  };

  const enforceMaxLength = () => {
    if (countCharacters() > maxLength) {
      editor.undoManager.undo();
      editor.notificationManager.open({
        text: `Maximum length of ${maxLength} characters exceeded.`,
        type: "warning",
        timeout: 2000
      });
    }
  };

  editor.on("keyup", enforceMaxLength);
  editor.on("paste", enforceMaxLength);
};

/**
 * For more formaters check https://www.tiny.cloud/docs/integrations/react/
 * @param value
 * @param onChange
 * @param onFocus
 * @param onBlur
 * @param modules
 * @param formats
 * @param placeholder
 * @param width
 * @param settings
 * @param notifyError
 * @returns {JSX.Element}
 * @constructor
 */
export const AdvancedWYSIWYGWithoutSagas = ({
  value: initialValue = "",
  onChange = noop,
  onFocus = noop,
  onBlur = noop,
  modules = "table media link code lists image codesample",
  formats = "bold italic underline | superscript subscript link | bullist numlist outdent indent | image | removeformat | code | codesample",
  placeholder = "Content...",
  width = "100%",
  height,
  settings = {},
  notifyError = noop,
  editorKey,
  ariaLabel = "Rich text editor",
  disabled,
  maxLength,
  ...props
}) => {
  const [useFallbackEditor, setUseFallbackEditor] = useState(false);

  let iniValueToUse = initialValue;

  // @NOTE: In some UI instances, we receive an object/value pair that we need to extract the value from
  if (
    iniValueToUse &&
    typeof iniValueToUse === "object" &&
    iniValueToUse.hasOwnProperty("value")
  ) {
    iniValueToUse = iniValueToUse.value;
  }

  const [inputValue, setInputValue] = useState(iniValueToUse);

  useEffect(() => {
    if (!equals(iniValueToUse, inputValue)) {
      setInputValue(iniValueToUse);
    }
  }, [iniValueToUse]);

  const handleOnChange = val => {
    setInputValue(val);
    onChange(val);
  };

  if (useFallbackEditor) {
    return (
      <TextAreaEditor
        {...{
          value: initialValue,
          onChange,
          onFocus,
          onBlur,
          maxLength,
          style: {
            width: "100%"
          },
          minRows: 10,
          ...props
        }}
      />
    );
  }

  return (
    <Editor
      key={editorKey}
      tabIndex={0}
      aria-label={ariaLabel}
      disabled={disabled}
      // eslint-disable-next-line no-underscore-dangle
      apiKey={window.__TINY_MCE_API_KEY__}
      onScriptsLoadError={err => {
        captureException(err, {
          extra: {
            message: "[TinyMCE onScriptsLoadError] - Advanced Without Sagas"
          }
        });
        setUseFallbackEditor(true);
      }}
      onFocus={() => onFocus(null)}
      onEditorChange={val => handleOnChange(val)}
      onBlur={() => onBlur(inputValue)}
      value={inputValue}
      init={{
        width,
        height,
        placeholder,
        plugins: modules,
        toolbar: formats,
        relative_urls: false,
        remove_script_host: false,
        file_picker_callback: callback =>
          handleImageUpload(callback, notifyError),
        setup: editor => {
          if (maxLength) {
            maxLengthPlugin(editor, maxLength);
          }
        },
        default_link_target: "_blank",
        paste_as_text: true,
        menubar: false,
        branding: false,
        image_caption: true,
        image_title: true,
        urlconverter_callback: function(url) {
          return url;
        },
        ...settings,
        external_plugins: {
          eqneditor: TINY_MCE_EXTERNAL_PLUGINS.eqneditor
        }
      }}
    />
  );
};
